import { motion } from "framer-motion";
import { TrendingUp, Cpu, Megaphone } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const PHONE =
  "https://images.unsplash.com/photo-1480694313141-fce5e697ee25?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxzbWFydHBob25lJTIwaW4lMjBkYXJrJTIwc3BvdGxpZ2h0fGVufDB8fHxibGFja3wxNzgzODYzMjMwfDA&ixlib=rb-4.1.0&q=85";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

const cards = [
  {
    icon: TrendingUp,
    tag: "Niche Finder",
    title: "Track any app's downloads & revenue",
    body: "Discover profitable, under-served niches before you build. See revenue estimates, competition and live store rankings.",
    href: "#niche",
    testId: "feature-niche",
  },
  {
    icon: Cpu,
    tag: "App Builder",
    title: "Build your app without coding",
    body: "Describe your idea in plain English and get a real, working app you can use live, refine by chatting, and publish.",
    href: "#chat",
    testId: "feature-builder",
  },
  {
    icon: Megaphone,
    tag: "Ad Library",
    title: "Find viral ad formats that convert",
    body: "Browse a library of winning ad creatives and formats from top apps, so you can copy what already works.",
    href: "#examples",
    testId: "feature-ads",
  },
];

export default function Features() {
  return (
    <section id="features" className="py-28 md:py-36 relative">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">/ the toolkit</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Everything you need to make money with apps.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.button
                key={c.tag}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                onClick={() => scrollToSection(c.href)}
                className="group text-left relative rounded-3xl border border-white/10 bg-[#0a0a0a] p-8 overflow-hidden hover:border-[#0055FF]/40 transition-colors duration-300 flex flex-col min-h-[300px]"
                data-testid={c.testId}
              >
                {i === 1 && (
                  <div className="absolute right-0 bottom-0 w-40 h-40 opacity-25 pointer-events-none">
                    <img src={PHONE} alt="" className="w-full h-full object-cover rounded-tl-3xl" />
                    <div className="absolute inset-0 bg-gradient-to-tl from-transparent to-[#0a0a0a]" />
                  </div>
                )}
                <div className="w-11 h-11 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center mb-6 relative">
                  <Icon size={20} className="text-[#00F0FF]" />
                </div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2 relative">{c.tag}</p>
                <h3 className="font-display font-bold text-2xl mb-3 relative leading-tight">{c.title}</h3>
                <p className="text-slate-400 leading-relaxed relative">{c.body}</p>
                <span className="mt-auto pt-6 font-mono text-xs text-[#00F0FF] opacity-0 group-hover:opacity-100 transition-opacity duration-300 relative">Explore →</span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
